import React, { useState, useEffect } from 'react';
import { Search, Database, FileText, ChevronLeft, ChevronRight, Trash2, Eye, AlertCircle, Users, ChevronDown } from 'lucide-react';
import { adminApiClient as api } from '../../services/api';

interface ChunkMetadata {
  chunk_id?: string;
  title?: string;
  page_range?: string;
  approx_tokens?: number;
  batch_number?: number;
  topics?: string[];
  key_concepts?: string[];
  section_hierarchy?: string;
  has_tables?: boolean;
  has_images?: boolean;
  has_code?: boolean;
  programming_language?: string;
  position?: string;
  related_chunks?: string[];
  extraction_method?: string;
  chunking_strategy?: string;
  context_before?: string;
  context_after?: string;
  document_name?: string;
  source_url?: string;
  // Allow arbitrary additional fields (brand, category, name, etc.)
  [key: string]: any;
}

interface Chunk {
  point_id: string;
  content: string;
  metadata: ChunkMetadata;
  content_length: number;
  content_preview: string;
  score?: number;  // Similarity score (only present in vector search results)
}

interface CollectionInfo {
  collection_name: string;
  points_count: number;
  bot_id: string;
  bot_name?: string;
  user_id: string;
  user_email?: string;
}

interface UserInfo {
  user_id: string;
  email?: string;
  bot_count: number;
  total_chunks: number;
}

const QdrantInspector: React.FC = () => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [collections, setCollections] = useState<CollectionInfo[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [selectedChunk, setSelectedChunk] = useState<Chunk | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalChunks, setTotalChunks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search/filter
  const [searchUser, setSearchUser] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [searchChunkId, setSearchChunkId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [vectorSearchQuery, setVectorSearchQuery] = useState('');
  const [isVectorSearchActive, setIsVectorSearchActive] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCollections();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-dropdown')) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isUserDropdownOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/qdrant/users');
      setUsers(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/qdrant/collections');
      setCollections(response.data.collections || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchChunks = async (botId: string, page: number = 1, textSearch?: string) => {
    try {
      setLoading(true);
      setError('');
      const params: any = { page, page_size: pageSize };

      // Add search_text parameter if provided
      if (textSearch && textSearch.trim()) {
        params.search_text = textSearch.trim();
      }

      const response = await api.get(`/admin/qdrant/chunks/${botId}`, {
        params
      });

      setChunks(response.data.chunks || []);
      setTotalChunks(response.data.total_chunks || 0);
      setTotalPages(response.data.total_pages || 0);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load chunks');
      setChunks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setSelectedBotId(''); // Reset bot selection
    setChunks([]);
    setSelectedChunk(null);
    setSearchText(''); // Clear text search
    setVectorSearchQuery(''); // Clear vector search
    setIsVectorSearchActive(false);
  };

  const handleSelectBot = (botId: string) => {
    setSelectedBotId(botId);
    setCurrentPage(1);
    setSelectedChunk(null);
    setSearchText(''); // Clear text search when changing bots
    setVectorSearchQuery(''); // Clear vector search when changing bots
    setIsVectorSearchActive(false);
    fetchChunks(botId, 1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && selectedBotId) {
      fetchChunks(selectedBotId, newPage, searchText);
    }
  };

  const handleTextSearch = () => {
    if (selectedBotId) {
      setCurrentPage(1); // Reset to page 1 when searching
      fetchChunks(selectedBotId, 1, searchText);
    }
  };

  const handleClearTextSearch = () => {
    setSearchText('');
    if (selectedBotId) {
      setCurrentPage(1);
      fetchChunks(selectedBotId, 1);
    }
  };

  const handleVectorSearch = async () => {
    if (!selectedBotId || !vectorSearchQuery.trim()) return;

    try {
      setLoading(true);
      setError('');
      setIsVectorSearchActive(true);

      const response = await api.get(`/admin/qdrant/chunks/${selectedBotId}/search`, {
        params: {
          query: vectorSearchQuery.trim(),
          limit: pageSize
        }
      });

      setChunks(response.data.chunks || []);
      setTotalChunks(response.data.total_chunks || 0);
      setTotalPages(1); // Vector search doesn't have pagination
      setCurrentPage(1);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to perform vector search');
      setChunks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearVectorSearch = () => {
    setVectorSearchQuery('');
    setIsVectorSearchActive(false);
    if (selectedBotId) {
      setCurrentPage(1);
      fetchChunks(selectedBotId, 1);
    }
  };

  const handleDeleteCollection = async (botId: string) => {
    if (!confirm('⚠️ Are you sure? This will permanently delete all chunks and data sources for this bot!')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/admin/qdrant/collection/${botId}`);
      alert('✅ Collection deleted successfully');

      // Refresh collections list
      await fetchCollections();

      // Clear selection if deleted bot was selected
      if (selectedBotId === botId) {
        setSelectedBotId('');
        setChunks([]);
        setSelectedChunk(null);
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete collection');
    } finally {
      setLoading(false);
    }
  };

  // Filter users by search (email and ID)
  const filteredUsers = users.filter(user => {
    if (!searchUser.trim()) return true;
    const searchLower = searchUser.toLowerCase();
    return (
      user.user_id.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Get bots for selected user from collections
  const userBots = selectedUserId
    ? collections.filter(col => col.user_id === selectedUserId)
    : [];

  // Get selected user info
  const selectedUserInfo = selectedUserId
    ? users.find(u => u.user_id === selectedUserId)
    : null;

  // Filter chunks by search
  const filteredChunks = chunks.filter(chunk => {
    if (!searchChunkId.trim()) return true;
    const searchLower = searchChunkId.toLowerCase();
    return (
      chunk.metadata.chunk_id?.toLowerCase().includes(searchLower) ||
      chunk.point_id.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Database className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Qdrant Vector Database Inspector</h1>
          </div>
          <p className="text-gray-600">Browse and inspect chunked PDF data stored in Qdrant</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* User Selection Dropdown */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User (Search by Email or ID)
            </label>

            {/* Searchable Dropdown */}
            <div className="relative user-dropdown">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or user ID..."
                  value={searchUser}
                  onChange={(e) => {
                    setSearchUser(e.target.value);
                    setIsUserDropdownOpen(true);
                  }}
                  onFocus={() => setIsUserDropdownOpen(true)}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>

              {/* Dropdown Results */}
              {isUserDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  {loading && users.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">Loading users...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchUser ? 'No users found matching your search' : 'No users available'}
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.user_id}
                        className={`p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                          selectedUserId === user.user_id ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          handleSelectUser(user.user_id);
                          setSearchUser(user.email || user.user_id);
                          setIsUserDropdownOpen(false);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {user.email || 'No email'}
                            </p>
                            <p className="text-xs text-gray-500 font-mono truncate mt-1">
                              ID: {user.user_id}
                            </p>
                          </div>
                          <div className="ml-4 flex flex-col items-end text-xs text-gray-600">
                            <span>{user.bot_count} bot{user.bot_count !== 1 ? 's' : ''}</span>
                            <span className="mt-1">{user.total_chunks.toLocaleString()} chunks</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Selected User Info */}
            {selectedUserInfo && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900">
                      Selected: {selectedUserInfo.email || 'No email'}
                    </p>
                    <p className="text-xs text-blue-700 font-mono mt-1">{selectedUserInfo.user_id}</p>
                  </div>
                  <div className="ml-4 text-sm text-blue-700">
                    {selectedUserInfo.bot_count} bots • {selectedUserInfo.total_chunks.toLocaleString()} chunks
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Middle Panel: Bots List */}
          <div className="lg:col-span-1">
            {!selectedUserId ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center h-full flex flex-col items-center justify-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a user to view their bots</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Bots</h2>
                  <span className="text-sm text-gray-500">{userBots.length} total</span>
                </div>

                {/* Bots List */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {userBots.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No bots found</div>
                  ) : (
                    userBots.map((col) => (
                      <div
                        key={col.bot_id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedBotId === col.bot_id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleSelectBot(col.bot_id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{col.bot_name || 'Unnamed Bot'}</p>
                            <p className="text-xs text-gray-500 font-mono truncate">{col.bot_id}</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCollection(col.bot_id);
                            }}
                            className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete collection"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {col.points_count} chunks
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel: Chunks */}
          <div className="lg:col-span-2">
            {!selectedBotId ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Select a bot collection to view chunks</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm">
                {/* Chunks Header */}
                <div className="p-6 border-b border-gray-200 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Chunks</h2>
                      <p className="text-sm text-gray-500">
                        {isVectorSearchActive ? (
                          <>
                            🎯 Showing top {filteredChunks.length} most relevant results (out of {totalChunks} total chunks)
                            <span className="block text-purple-600 font-medium mt-1">
                              Query: "{vectorSearchQuery}" - Ranked by semantic similarity
                            </span>
                          </>
                        ) : (
                          <>
                            Showing {filteredChunks.length} of {totalChunks} total chunks
                            {searchText && ` (text search: "${searchText}")`}
                            {searchChunkId && !searchText && ` (filtered by ID)`}
                          </>
                        )}
                      </p>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1 || loading}
                          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages || loading}
                          className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Search by Chunk ID */}
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by Chunk ID or Point ID..."
                      value={searchChunkId}
                      onChange={(e) => setSearchChunkId(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    {searchChunkId && (
                      <button
                        onClick={() => setSearchChunkId('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Search by Text Content */}
                  <div className="flex gap-2">
                    <div className="relative flex-1 max-w-2xl">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by text content (exact text match)..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleTextSearch();
                          }
                        }}
                        disabled={isVectorSearchActive}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                    <button
                      onClick={handleTextSearch}
                      disabled={!searchText.trim() || loading || isVectorSearchActive}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      Search
                    </button>
                    {searchText && (
                      <button
                        onClick={handleClearTextSearch}
                        disabled={isVectorSearchActive}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Vector Similarity Search */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex gap-2">
                      <div className="relative flex-1 max-w-2xl">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-400" />
                        <input
                          type="text"
                          placeholder="🔍 Semantic search with relevance scores (e.g., 'shopping cart issues')..."
                          value={vectorSearchQuery}
                          onChange={(e) => setVectorSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleVectorSearch();
                            }
                          }}
                          className="w-full pl-10 pr-4 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-purple-50"
                        />
                      </div>
                      <button
                        onClick={handleVectorSearch}
                        disabled={!vectorSearchQuery.trim() || loading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        🎯 Vector Search
                      </button>
                      {isVectorSearchActive && (
                        <button
                          onClick={handleClearVectorSearch}
                          className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 text-sm font-medium"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-purple-700 mt-1 ml-1">
                      💡 Vector search finds semantically similar content (AI-powered) with relevance scores
                    </p>
                  </div>
                </div>

                {/* Chunks List */}
                <div className="p-6 space-y-4 max-h-[700px] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-12 text-gray-500">
                      {isVectorSearchActive ? '🎯 Performing vector similarity search...' : searchText ? 'Searching through chunks...' : 'Loading chunks...'}
                    </div>
                  ) : filteredChunks.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      {isVectorSearchActive ? `No semantically similar chunks found for "${vectorSearchQuery}"` : searchText ? `No chunks found containing "${searchText}"` : searchChunkId ? 'No chunks match your ID search' : 'No chunks found'}
                    </div>
                  ) : (
                    filteredChunks.map((chunk) => (
                      <div
                        key={chunk.point_id}
                        className={`border-2 rounded-lg p-4 transition-colors ${
                          chunk.score !== undefined
                            ? 'border-purple-300 bg-purple-50/30'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {/* Relevance Score Banner (only in vector search) */}
                        {chunk.score !== undefined && (
                          <div className="mb-3 pb-3 border-b-2 border-purple-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">🎯</span>
                                <div>
                                  <div className="text-sm font-semibold text-purple-900">Relevance Score</div>
                                  <div className="text-xs text-purple-700">Semantic similarity match</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-3xl font-bold text-purple-600">
                                  {(chunk.score * 100).toFixed(1)}%
                                </div>
                                <div className="text-xs text-purple-700">
                                  {chunk.score >= 0.9 ? 'Excellent' :
                                   chunk.score >= 0.75 ? 'Very Good' :
                                   chunk.score >= 0.6 ? 'Good' :
                                   'Moderate'}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Chunk Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-1">
                              {chunk.metadata.title || 'Untitled Chunk'}
                            </h3>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                              {/* Document Source - Prominently displayed */}
                              {chunk.metadata.document_name && (
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-semibold">📄 {chunk.metadata.document_name}</span>
                              )}
                              {chunk.metadata.source_url && (
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-semibold truncate max-w-xs">🔗 {chunk.metadata.source_url}</span>
                              )}
                              {chunk.metadata.chunk_id && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-mono">ID: {chunk.metadata.chunk_id}</span>
                              )}
                              {chunk.metadata.page_range && (
                                <span className="px-2 py-1 bg-gray-100 rounded">Pages: {chunk.metadata.page_range}</span>
                              )}
                              {chunk.metadata.approx_tokens && (
                                <span className="px-2 py-1 bg-gray-100 rounded">~{chunk.metadata.approx_tokens} tokens</span>
                              )}
                              {chunk.metadata.batch_number && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Batch {chunk.metadata.batch_number}</span>
                              )}
                              {chunk.metadata.position && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Position: {chunk.metadata.position}</span>
                              )}
                              {chunk.metadata.related_chunks && chunk.metadata.related_chunks.length > 0 && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{chunk.metadata.related_chunks.length} related</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedChunk(chunk)}
                            className="ml-2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View full details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Metadata Tags */}
                        {(chunk.metadata.topics && chunk.metadata.topics.length > 0) && (
                          <div className="mb-2">
                            <p className="text-xs font-medium text-gray-700 mb-1">Topics:</p>
                            <div className="flex flex-wrap gap-1">
                              {chunk.metadata.topics.slice(0, 5).map((topic, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                  {topic}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Content Preview */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-sm text-gray-600 line-clamp-3">{chunk.content_preview}...</p>
                          <p className="text-xs text-gray-400 mt-1">{chunk.content_length} characters</p>
                        </div>

                        {/* Flags */}
                        <div className="flex gap-2 mt-2">
                          {chunk.metadata.has_tables && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Has Tables</span>
                          )}
                          {chunk.metadata.has_images && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">Has Images</span>
                          )}
                          {chunk.metadata.has_code && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded">
                              Code: {chunk.metadata.programming_language || 'Unknown'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chunk Details Modal */}
        {selectedChunk && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Chunk Details</h2>
                  <button
                    onClick={() => setSelectedChunk(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Metadata */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Metadata</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    {/* Document Source - Prominently at top */}
                    {(selectedChunk.metadata.document_name || selectedChunk.metadata.source_url) && (
                      <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        {selectedChunk.metadata.document_name && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700 font-semibold">📄 Document:</span>
                            <span className="text-indigo-700 font-semibold">{selectedChunk.metadata.document_name}</span>
                          </div>
                        )}
                        {selectedChunk.metadata.source_url && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-gray-700 font-semibold">🔗 Source:</span>
                            <a href={selectedChunk.metadata.source_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate">
                              {selectedChunk.metadata.source_url}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-600">Point ID:</span>
                        <span className="ml-2 font-mono text-xs">{selectedChunk.point_id}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Chunk ID:</span>
                        <span className="ml-2 font-mono text-xs">{selectedChunk.metadata.chunk_id || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Title:</span>
                        <span className="ml-2">{selectedChunk.metadata.title || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Page Range:</span>
                        <span className="ml-2">{selectedChunk.metadata.page_range || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Tokens:</span>
                        <span className="ml-2">{selectedChunk.metadata.approx_tokens || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Batch:</span>
                        <span className="ml-2">{selectedChunk.metadata.batch_number || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Position:</span>
                        <span className="ml-2">{selectedChunk.metadata.position || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Extraction Method:</span>
                        <span className="ml-2">{selectedChunk.metadata.extraction_method || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Chunking Strategy:</span>
                        <span className="ml-2">{selectedChunk.metadata.chunking_strategy || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Related Chunks:</span>
                        <span className="ml-2">{selectedChunk.metadata.related_chunks?.length || 0}</span>
                      </div>
                    </div>

                    {/* Related Chunks List */}
                    {selectedChunk.metadata.related_chunks && selectedChunk.metadata.related_chunks.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-600 mb-2">Related Chunk IDs:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedChunk.metadata.related_chunks.map((relatedId, idx) => (
                            <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-mono">
                              {relatedId}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chunk Summary */}
                {selectedChunk.metadata.chunk_summary && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Chunk Summary</h3>
                    <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3">
                      <p className="text-sm text-emerald-800 leading-relaxed">
                        {selectedChunk.metadata.chunk_summary}
                      </p>
                    </div>
                  </div>
                )}

                {/* Topics & Key Concepts */}
                {(selectedChunk.metadata.topics || selectedChunk.metadata.key_concepts) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Topics & Concepts</h3>
                    <div className="space-y-3">
                      {selectedChunk.metadata.topics && selectedChunk.metadata.topics.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Topics:</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedChunk.metadata.topics.map((topic, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedChunk.metadata.key_concepts && selectedChunk.metadata.key_concepts.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Key Concepts:</p>
                          <div className="flex flex-wrap gap-1">
                            {selectedChunk.metadata.key_concepts.map((concept, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                {concept}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Custom Metadata Fields (CSV fields, etc.) */}
                {(() => {
                  // Define system fields that shouldn't appear in custom section
                  const systemFields = [
                    'chunk_id', 'title', 'page_range', 'approx_tokens', 'batch_number',
                    'topics', 'key_concepts', 'section_hierarchy', 'has_tables', 'has_images',
                    'has_code', 'programming_language', 'position', 'related_chunks',
                    'extraction_method', 'chunking_strategy', 'context_before', 'context_after',
                    'document_name', 'source_url', 'chunk_summary', '_position', '_context_before',
                    '_context_after', '_related_chunks'
                  ];

                  // Extract custom fields
                  const customFields = Object.entries(selectedChunk.metadata)
                    .filter(([key, value]) =>
                      !systemFields.includes(key) &&
                      value !== null &&
                      value !== undefined &&
                      value !== ''
                    );

                  if (customFields.length === 0) return null;

                  return (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom Metadata</h3>
                      <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 space-y-2">
                        <div className="grid grid-cols-2 gap-4">
                          {customFields.map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-600 font-medium capitalize">
                                {key.replace(/_/g, ' ')}:
                              </span>
                              <span className="ml-2 text-gray-900">
                                {Array.isArray(value) ? (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {value.map((item, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded">
                                        {String(item)}
                                      </span>
                                    ))}
                                  </div>
                                ) : typeof value === 'object' ? (
                                  JSON.stringify(value)
                                ) : (
                                  String(value)
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Context Fields */}
                {(selectedChunk.metadata.context_before || selectedChunk.metadata.context_after) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Chunk Context</h3>
                    <div className="space-y-3">
                      {selectedChunk.metadata.context_before && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1 font-semibold">Context Before:</p>
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <p className="text-sm text-gray-700 italic">{selectedChunk.metadata.context_before}</p>
                          </div>
                        </div>
                      )}
                      {selectedChunk.metadata.context_after && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1 font-semibold">Context After:</p>
                          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                            <p className="text-sm text-gray-700 italic">{selectedChunk.metadata.context_after}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Full Content */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Full Content ({selectedChunk.content_length} characters)</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">{selectedChunk.content}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QdrantInspector;
